/**
 * LoadingSkeleton Component
 * Provides accessible loading states with skeleton screens
 */

import React from 'react';
import { makeStyles, Skeleton, SkeletonItem } from '@fluentui/react-components';

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    padding: '20px',
  },
  row: {
    display: 'flex',
    gap: '12px',
    alignItems: 'center',
  },
  cardSkeleton: {
    padding: '20px',
    backgroundColor: '#ffffff',
    borderRadius: '8px',
    border: '1px solid #e1dfdd',
  },
  tableSkeleton: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  gridSkeleton: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
    gap: '16px',
  },
});

interface LoadingSkeletonProps {
  type?: 'card' | 'table' | 'grid' | 'list';
  rows?: number;
  appearance?: 'opaque' | 'translucent';
}

export const LoadingSkeleton: React.FC<LoadingSkeletonProps> = ({
  type = 'list',
  rows = 5,
  appearance = 'translucent',
}) => {
  const styles = useStyles();

  // Card Skeleton
  if (type === 'card') {
    return (
      <div className={styles.cardSkeleton} role="status" aria-label="Loading content">
        <Skeleton appearance={appearance}>
          <SkeletonItem size={24} style={{ marginBottom: '12px', width: '60%' }} />
          <SkeletonItem size={16} style={{ marginBottom: '8px' }} />
          <SkeletonItem size={16} style={{ marginBottom: '8px', width: '80%' }} />
          <SkeletonItem size={16} style={{ width: '70%' }} />
        </Skeleton>
      </div>
    );
  }

  // Table Skeleton
  if (type === 'table') {
    return (
      <div className={styles.tableSkeleton} role="status" aria-label="Loading table data">
        <Skeleton appearance={appearance}>
          {/* Header */}
          <div className={styles.row}>
            <SkeletonItem size={32} style={{ width: '40px' }} />
            <SkeletonItem size={20} style={{ flex: 1 }} />
            <SkeletonItem size={20} style={{ flex: 1 }} />
            <SkeletonItem size={20} style={{ flex: 1 }} />
            <SkeletonItem size={20} style={{ width: '100px' }} />
          </div>
          {/* Rows */}
          {Array.from({ length: rows }).map((_, index) => (
            <div key={index} className={styles.row}>
              <SkeletonItem shape="circle" size={32} />
              <SkeletonItem size={16} style={{ flex: 1 }} />
              <SkeletonItem size={16} style={{ flex: 1 }} />
              <SkeletonItem size={16} style={{ flex: 1 }} />
              <SkeletonItem size={16} style={{ width: '100px' }} />
            </div>
          ))}
        </Skeleton>
      </div>
    );
  }

  // Grid Skeleton
  if (type === 'grid') {
    return (
      <div className={styles.gridSkeleton} role="status" aria-label="Loading grid items">
        <Skeleton appearance={appearance}>
          {Array.from({ length: rows }).map((_, index) => (
            <div key={index} className={styles.cardSkeleton}>
              <SkeletonItem size={24} style={{ marginBottom: '12px', width: '70%' }} />
              <SkeletonItem size={16} style={{ marginBottom: '8px' }} />
              <SkeletonItem size={16} style={{ width: '60%' }} />
            </div>
          ))}
        </Skeleton>
      </div>
    );
  }

  // List Skeleton (default)
  return (
    <div className={styles.container} role="status" aria-label="Loading list">
      <Skeleton appearance={appearance}>
        {Array.from({ length: rows }).map((_, index) => (
          <div key={index} className={styles.row}>
            <SkeletonItem shape="circle" size={40} />
            <div style={{ flex: 1 }}>
              <SkeletonItem size={16} style={{ marginBottom: '8px', width: '60%' }} />
              <SkeletonItem size={12} style={{ width: '40%' }} />
            </div>
          </div>
        ))}
      </Skeleton>
    </div>
  );
};

export default LoadingSkeleton;
